import {
  Component,
  HostBinding,
  NgZone,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
  Inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { fromEvent, Subject, Observable } from 'rxjs';
import {
  throttleTime,
  map,
  distinctUntilChanged,
  takeUntil,
  pairwise,
  shareReplay,
} from 'rxjs/operators';
import { DesktopLayoutComponent } from '../desktop-layout/desktop-layout.component';
import { MobileLayoutComponent } from '../mobile-layout/mobile-layout.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
  selector: 'app-adaptive-main-layout',
  imports: [
    CommonModule,
    RouterOutlet,
    DesktopLayoutComponent,
    MobileLayoutComponent,
  ],
  templateUrl: './adaptive-main-layout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./adaptive-main-layout.component.css'],
})
export class AdaptiveMainLayoutComponent implements OnInit, OnDestroy {
  isMobile$: Observable<boolean>;
  private readonly SCROLL_THRESHOLD = 15;
  private destroy$ = new Subject<void>();

  @HostBinding('class.scrolling-down')
  isScrollingDown = false;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isMobile$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
      map((result) => result.matches),
      shareReplay(),
    );
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        fromEvent(window, 'scroll')
          .pipe(
            throttleTime(100, undefined, { leading: true, trailing: true }),
            map(() => window.scrollY),
            pairwise(),
            map(([prevY, currentY]) => {
              const delta = currentY - prevY;
              if (currentY <= 50) return false;
              if (Math.abs(delta) < this.SCROLL_THRESHOLD)
                return this.isScrollingDown;
              return delta > 0;
            }),
            distinctUntilChanged(),
            takeUntil(this.destroy$),
          )
          .subscribe((isScrollingDown) => {
            this.zone.run(() => {
              this.isScrollingDown = isScrollingDown;
            });
          });
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
